import React from 'react';
import Footer from '../components/Footer';

export default function ChangelogView() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-between text-gray-200">
      <div className="grow flex flex-col items-center justify-start w-full px-4 py-12">
        <div className="w-full max-w-4xl prose prose-invert prose-blue">
          
            <h1>Changelog</h1>
<p>All notable changes to the SnipIT SaaS Ecosystem are documented here.</p>
<hr />
<h2>v3.0.0 — 2026-06-13</h2>
<h3>Major Architectural Shift: The Monorepo Ecosystem</h3>
<p>SnipIT is no longer just a Chrome Extension. It has been completely rebuilt as an enterprise SaaS platform utilizing a modern Monorepo structure (npm workspaces).</p>
<h3>Added 3.0.0</h3>
<ul>
<li><strong>Web Dashboard (<code>apps/web</code>)</strong> — A primary React/Vite Progressive Web App (PWA) managing user authentication, organization settings, role-based access control, Stripe subscription billing, and a new community Developer Board.</li>
<li><strong>Native Desktop App (<code>apps/desktop</code>)</strong> — A blazing fast native Windows/macOS client built with Tauri and Rust. Offers a persistent &quot;always-on-top&quot; experience for lightning-fast snippet extraction without relying on a browser.</li>
<li><strong>Shared Core (<code>packages/core</code>)</strong> — Abstracted all syntax formatters, AST parsers, local storage logic, and hotkeys into a single source-of-truth package.</li>
<li><strong>Shared UI Library (<code>packages/ui</code>)</strong> — A unified design system utilizing Tailwind CSS and Lucide React components to ensure pixel-perfect consistency across all three platforms.</li>
</ul>
<h3>Changed 3.0.0</h3>
<ul>
<li>The original Chrome Extension is now nested under <code>apps/extension</code>.</li>
<li><strong>Licensing Model:</strong> Replaced the open-source MIT license with a strict Proprietary Commercial License (EULA) to protect intellectual property and support the new SaaS billing model.</li>
<li><strong>Documentation Overhaul:</strong> Established a massive root-level <code>README.md</code>, an InfoSec-ready <code>SECURITY.md</code> detailing Rust and CSP boundaries, and a <code>PRIVACY.md</code> highlighting the local-first architecture.</li>
</ul>
<hr />
<h2>v2.1.0 — 2026-05-24</h2>
<h3>Added 2.1.0</h3>
<ul>
<li><strong>Tools Vault</strong> — a new dedicated vault (Amber theme) for common developer utilities.</li>
<li><strong>Regex Tester tool</strong> — test regular expressions against input text with live highlighting and custom output wrappers.</li>
<li><strong>JWT Decoder tool</strong> — decode JSON Web Tokens entirely offline to view headers and payloads.</li>
<li><strong>JSON Formatter tool</strong> — format and prettify raw JSON strings into readable syntax.</li>
<li><strong>Boilerplate Gen tool</strong> — instantly generate common project boilerplate files and layouts.</li>
<li><strong>Base64 &amp; URL tool</strong> — encode and decode Base64 and URL strings.</li>
<li><strong>Color Converter tool</strong> — convert between HEX, RGB, HSL, and other color formats.</li>
<li><strong>Hash Generator tool</strong> — generate secure hashes like MD5, SHA-1, and SHA-256.</li>
<li><strong>Mock Data Gen tool</strong> — generate mock JSON/CSV data payloads.</li>
<li><strong>Case Converter tool</strong> — convert text casing styles.</li>
<li><strong>Markdown Pad tool</strong> — live markdown editor and previewer.</li>
<li><strong>Secret Scanner</strong> — a pre-save security guardrail that uses local regex checks to warn users if they attempt to save sensitive credentials (like API keys or Bearer tokens).</li>
</ul>
<hr />
<h2>v2.0.0 — 2026-05-17</h2>
<h3>Added 2.0.0</h3>
<ul>
<li><strong>Links Vault</strong> — a third dedicated vault for storing developer links, cURL commands, and documentation references, with a Smart Grab feature to capture the active tab's URL and title.</li>
<li><strong>Triple-Vault theming</strong> — dynamic color themes per vault (Blue/Dev, Purple/AI, Green/Links).</li>
<li><strong>Tag system</strong> — add custom <code>#tags</code> to any snippet for cross-vault filtering.</li>
<li><strong>Version history</strong> — up to 5 previous versions of any snippet content are retained and restorable from the editor.</li>
<li><strong>Few-Shot prompt type</strong> — structured form with a Pattern Template input and example pairs area.</li>
<li><strong>Context prompt type</strong> — dedicated Source URL field alongside raw context data.</li>
<li><strong>Import schema validation</strong> — imported backup files are now validated against the known data schema and sanitized with DOMPurify before being written to storage.</li>
<li><strong>URL protocol validation</strong> — all links opened from the Links Vault are validated against an <code>http</code>/<code>https</code> allowlist before being passed to <code>window.open()</code>, with <code>noopener,noreferrer</code> applied.</li>
<li><strong>React Error Boundary</strong> — prevents white-screen-of-death on component-level errors.</li>
<li><strong>Hotkey stability</strong> — keyMap is now memoized to prevent unnecessary event listener re-registration on every render.</li>
</ul>
<h3>Changed 2.0.0</h3>
<ul>
<li>Upgraded from a dual-vault (Code + AI) to a <strong>triple-vault</strong> architecture.</li>
<li>Upgraded to <strong>Vite 8.x</strong> build toolchain.</li>
<li><code>manifest.json</code> and <code>package.json</code> versions aligned to <code>2.0.0</code>.</li>
</ul>
<h3>Security 2.0.0</h3>
<ul>
<li>DOMPurify (<code>^3.4.3</code>) applied at render time and at import time (defense-in-depth).</li>
<li><code>window.open()</code> now enforces strict protocol validation and <code>noopener,noreferrer</code>.</li>
<li>Import handler validates all field types and caps string lengths before persisting to <code>chrome.storage.local</code>.</li>
<li><code>dist/</code> excluded from version control for clean, auditable builds.</li>
</ul>
<hr />
<h2>v1.0.0 — 2023-08-20</h2>
<h3>Added 1.0.0</h3>
<ul>
<li>Dual-vault Chrome Side Panel extension (Code Vault + AI Vault).</li>
<li>Smart code formatter for AppSheet, Google Sheets, JavaScript, SQL, and JSON.</li>
<li>Web clipper via content script message passing.</li>
<li>DOMPurify XSS sanitization on all rendered snippet content.</li>
<li>Manifest V3 CSP compliance (<code>script-src 'self'; object-src 'self'</code>).</li>
<li>PrismJS syntax highlighting with custom AppSheet and Sheets grammars.</li>
<li>Export/import backup as <code>.json</code>.</li>
<li>Global hotkeys: <code>Ctrl+K</code>, <code>Ctrl+N</code>, <code>Ctrl+S</code>, <code>Esc</code>.</li>
<li><strong>Gem prompt type</strong> — 4-part structured form (Premise, Persona, Context, Rules).</li>
<li><strong>System Prompt type</strong> — with Temperature slider.</li>
</ul>

        
        </div>
      </div>
      <Footer />
    </div>
  );
}
